import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@p81-common/ui';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';

interface Addon {
  id: string;
  friendlyName: string;
  included: boolean;
  quantity: number | null;
  unit: string | null;
}

interface AddonsListProps {
  addons: Addon[];
  error?: boolean;
}

const AddonsList = ({ addons, error = false }: AddonsListProps) => {
  const { t } = useTranslation('activity');

  const heading = (
    <Typography
      variant="h4"
      mb={1.5}
      sx={{ color: '#394463', fontSize: '20px' }}
    >
      {t('activity:customers:ADDONS_HEADING')}
    </Typography>
  );

  if (error) {
    return (
      <Box>
        {heading}
        <Typography variant="body2" color="error" data-testid="addons-error">
          {t('activity:customers:ADDONS_ERROR')}
        </Typography>
      </Box>
    );
  }

  if (addons.length === 0) {
    return (
      <Box>
        {heading}
        <Typography variant="body2" color="text.secondary">
          {t('activity:customers:ADDONS_EMPTY_STATE')}
        </Typography>
      </Box>
    );
  }

  // Sort: included first, then by friendlyName A→Z within each group
  const sorted = [...addons].sort((a, b) => {
    if (a.included !== b.included) return a.included ? -1 : 1;
    return a.friendlyName.localeCompare(b.friendlyName);
  });

  return (
    <Box>
      {heading}
      <Box component="ul" sx={{ listStyle: 'none', m: 0, py: 0, pl: 1.5 }}>
        {sorted.map(addon => (
          <Box
            key={addon.id}
            component="li"
            data-testid="addon-item"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              minHeight: 48,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-child': { borderBottom: 'none' }
            }}
          >
            <Typography
              variant="body2"
              component="span"
              sx={{ color: addon.included ? 'text.primary' : 'text.secondary' }}
            >
              {addon.friendlyName}
              {addon.quantity !== null && (
                <Box component="span" sx={{ color: '#3e88ff', ml: 0.5 }}>
                  ({addon.quantity}
                  {addon.unit ? ` ${addon.unit}` : ''})
                </Box>
              )}
            </Typography>
            {addon.included ? (
              <CheckCircleRoundedIcon
                fontSize="small"
                titleAccess={t('activity:customers:ADDON_INCLUDED')}
                data-testid="addon-status-included"
                sx={{ color: '#44D7B6' }}
              />
            ) : (
              <CancelRoundedIcon
                fontSize="small"
                titleAccess={t('activity:customers:ADDON_NOT_INCLUDED')}
                data-testid="addon-status-not-included"
                sx={{ color: '#E8EDF5' }}
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AddonsList;
